import { throwErrorMessage } from 'dashboard/store/utils/api';
import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import * as types from '../mutation-types';
import CannedResponseAPI from '../../api/cannedResponse';

// Monotonic token ensures only the most recent getCannedResponse dispatch
// commits its results. Protects against out-of-order responses when a later
// search supersedes an earlier still-in-flight one.
let latestGetRequestToken = 0;

const state = {
  records: [],
  meta: {
    count: 0,
    currentPage: 1,
    perPage: 25,
  },
  uiFlags: {
    fetchingList: false,
    fetchingItem: false,
    creatingItem: false,
    updatingItem: false,
    deletingItem: false,
  },
};

const getters = {
  getCannedResponses(_state) {
    return _state.records;
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getCannedResponsesMeta(_state) {
    return _state.meta;
  },
};

const actions = {
  getCannedResponse: async function getCannedResponse(
    { commit },
    { searchKey, page = 1, perPage, sortOrder } = {}
  ) {
    latestGetRequestToken += 1;
    const requestToken = latestGetRequestToken;
    commit(types.default.SET_CANNED_UI_FLAG, { fetchingList: true });
    try {
      const response = await CannedResponseAPI.get({
        searchKey,
        page,
        perPage,
        sortOrder,
      });
      if (requestToken !== latestGetRequestToken) return;
      commit(types.default.SET_CANNED, response.data.payload);
      commit(types.default.SET_CANNED_META, response.data.meta);
      commit(types.default.SET_CANNED_UI_FLAG, { fetchingList: false });
    } catch (error) {
      if (requestToken === latestGetRequestToken) {
        commit(types.default.SET_CANNED_UI_FLAG, { fetchingList: false });
      }
    }
  },

  // Reset the shared records list and invalidate any in-flight getCannedResponse
  // dispatch. Called from CannedResponse.vue#created so the next first render
  // never reads stale results committed by a previous instance that unmounted
  // mid-flight (the token check alone cannot drop those, because no newer
  // dispatch has bumped the counter yet).
  resetCannedResponses: function resetCannedResponses({ commit }) {
    latestGetRequestToken += 1;
    commit(types.default.SET_CANNED, []);
    commit(types.default.SET_CANNED_UI_FLAG, { fetchingList: false });
  },

  createCannedResponse: async function createCannedResponse(
    { commit },
    cannedObj
  ) {
    commit(types.default.SET_CANNED_UI_FLAG, { creatingItem: true });
    try {
      const response = await CannedResponseAPI.create(cannedObj);
      commit(types.default.ADD_CANNED, response.data);
      commit(types.default.SET_CANNED_UI_FLAG, { creatingItem: false });
      return response.data;
    } catch (error) {
      commit(types.default.SET_CANNED_UI_FLAG, { creatingItem: false });
      return throwErrorMessage(error);
    }
  },

  updateCannedResponse: async function updateCannedResponse(
    { commit },
    { id, ...updateObj }
  ) {
    commit(types.default.SET_CANNED_UI_FLAG, { updatingItem: true });
    try {
      const response = await CannedResponseAPI.update(id, updateObj);
      commit(types.default.EDIT_CANNED, response.data);
      commit(types.default.SET_CANNED_UI_FLAG, { updatingItem: false });
      return response.data;
    } catch (error) {
      commit(types.default.SET_CANNED_UI_FLAG, { updatingItem: false });
      return throwErrorMessage(error);
    }
  },

  deleteCannedResponse: async function deleteCannedResponse({ commit }, id) {
    commit(types.default.SET_CANNED_UI_FLAG, { deletingItem: true });
    try {
      await CannedResponseAPI.delete(id);
      commit(types.default.DELETE_CANNED, id);
      commit(types.default.SET_CANNED_UI_FLAG, { deletingItem: false });
      return id;
    } catch (error) {
      commit(types.default.SET_CANNED_UI_FLAG, { deletingItem: false });
      return throwErrorMessage(error);
    }
  },
};

const mutations = {
  [types.default.SET_CANNED_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.default.SET_CANNED]: MutationHelpers.set,
  [types.default.SET_CANNED_META](_state, data) {
    const { count, current_page: currentPage, per_page: perPage } = data;
    _state.meta.count = count;
    _state.meta.currentPage = currentPage;
    if (perPage) _state.meta.perPage = perPage;
  },
  [types.default.ADD_CANNED]: MutationHelpers.create,
  [types.default.EDIT_CANNED]: MutationHelpers.update,
  [types.default.DELETE_CANNED]: MutationHelpers.destroy,
};

export default {
  state,
  getters,
  actions,
  mutations,
};
